import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { signOut } from 'firebase/auth';
import { getInstructorSchedule, cancelLesson } from '../services/googleCalendar';
import { saveLogEntry } from '../services/googleSheets';
import { optimizeFeedback, PRESET_TEMPLATES } from '../services/openai';

function InstructorDashboard() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedule, setSchedule] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // 加载当日课程安排
  useEffect(() => {
    loadSchedule();
  }, [selectedDate]);

  const loadSchedule = async () => {
    try {
      const instructorId = auth.currentUser?.uid;
      const data = await getInstructorSchedule(instructorId, selectedDate);
      setSchedule(data);
    } catch (error) {
      console.error('加载课程失败:', error);
    }
  };

  // 提交评语
  const handleSubmitFeedback = async () => {
    if (!feedback.trim() || !selectedStudent) return;

    setLoading(true);
    try {
      // 1. AI 优化评语
      const optimized = await optimizeFeedback(feedback, selectedStudent.level);
      
      // 2. 保存到 Google Sheets
      await saveLogEntry({
        date: new Date().toISOString(),
        studentId: selectedStudent.id,
        studentName: selectedStudent.name,
        instructorId: auth.currentUser?.uid,
        instructorName: auth.currentUser?.displayName,
        feedback: feedback,
        optimizedFeedback: optimized.optimized,
        keyPoints: optimized.keyPoints,
        hours: 2, // TODO: 可调整
        status: 'completed'
      });

      alert('评语提交成功！');
      setFeedback('');
      setSelectedStudent(null);
      loadSchedule();
    } catch (error) {
      console.error('提交失败:', error);
      alert('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 插入预设模板
  const insertTemplate = (template) => {
    setFeedback(prev => prev + (prev ? ' ' : '') + template.text);
    setShowTemplates(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Instructor Dashboard</h1>
            <p className="text-sm text-gray-600">Welcome, {auth.currentUser?.displayName || 'Instructor'}</p>
          </div>
          <button
            onClick={() => signOut(auth)}
            className="btn-secondary"
          >
            Sign Out
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧: 今日课程 */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Today's Schedule</h2>
              
              {/* 日期选择 */}
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                className="input mb-4"
              />

              {/* 课程列表 */}
              <div className="space-y-3">
                {schedule.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No lessons scheduled for today</p>
                ) : (
                  schedule.map((lesson) => (
                    <div
                      key={lesson.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-colors"
                      onClick={() => setSelectedStudent(lesson.student)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900">{lesson.student.name}</h3>
                          <p className="text-sm text-gray-600">学员 ID: {lesson.student.id}</p>
                          <p className="text-sm text-gray-600">时间: {lesson.time}</p>
                        </div>
                        <span className={`badge ${
                          lesson.status === 'completed' ? 'badge-success' :
                          lesson.status === 'scheduled' ? 'badge-warning' :
                          'badge-danger'
                        }`}>
                          {lesson.status === 'completed' ? '已完成' :
                           lesson.status === 'scheduled' ? '已预约' :
                           '已取消'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 填写评语 */}
            {selectedStudent && (
              <div className="card fade-in">
                <h2 className="text-lg font-semibold mb-4">Write Feedback - {selectedStudent.name}</h2>
                
                <div className="space-y-4">
                  {/* 快捷模板 */}
                  <div>
                    <button
                      onClick={() => setShowTemplates(!showTemplates)}
                      className="btn-secondary text-sm"
                    >
                      📝 Quick Templates
                    </button>
                    
                    {showTemplates && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">优点表扬:</p>
                          <div className="flex flex-wrap gap-2">
                            {PRESET_TEMPLATES.praise.map((t) => (
                              <button
                                key={t.code}
                                onClick={() => insertTemplate(t)}
                                className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded hover:bg-green-200"
                                title={t.cn}
                              >
                                {t.code}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1">需改进:</p>
                          <div className="flex flex-wrap gap-2">
                            {PRESET_TEMPLATES.improve.map((t) => (
                              <button
                                key={t.code}
                                onClick={() => insertTemplate(t)}
                                className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded hover:bg-yellow-200"
                                title={t.cn}
                              >
                                {t.code}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 评语输入 */}
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Enter today's lesson feedback (Chinese/English supported, AI will optimize automatically)"
                    className="input min-h-[150px] resize-y"
                  />

                  {/* 提交按钮 */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleSubmitFeedback}
                      disabled={loading || !feedback.trim()}
                      className="btn-primary flex-1"
                    >
                      {loading ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedStudent(null);
                        setFeedback('');
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 右侧: 统计信息 */}
          <div className="space-y-4">
            <div className="card">
              <h3 className="font-semibold mb-3">Today's Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Lessons:</span>
                  <span className="font-semibold">{schedule.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Completed:</span>
                  <span className="font-semibold text-green-600">
                    {schedule.filter(s => s.status === 'completed').length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Scheduled:</span>
                  <span className="font-semibold text-yellow-600">
                    {schedule.filter(s => s.status === 'scheduled').length}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full btn-secondary text-left">
                  📅 View This Week
                </button>
                <button className="w-full btn-secondary text-left">
                  📊 My Students
                </button>
                <button className="w-full btn-secondary text-left">
                  📝 Feedback History
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InstructorDashboard;
